import { officeExtentions as officeExtentionsType } from "../../../src/taskpane/config/constants";
import { FilterSection } from "../../../src/taskpane/redux/models/reduxModels";

// Single source of truth for how a pre-select querystring key resolves to a list
// field, and for the field-type predicates the pre-select pipeline branches on.
// The fast path (preselectFilterSynthesizer) and the reconcile
// (preselectFilterResolver) MUST resolve keys identically — the fast-vs-reconciled
// query signature comparison assumes it — so both consume these helpers instead of
// keeping local copies that could drift.

export const DOCICON_FIELD_NAME = "DocIcon";

// Null prototype, like the office-bucket lookup below: keys come straight from
// the querystring, so a key named like an Object.prototype member
// ("constructor", "toString", …) must MISS the alias table — a plain object
// returns the inherited member, which survives the `??` (not nullish) and
// crashes matchesFieldKey, killing the entire deep link including its valid
// fields.
const FIELD_ALIASES: Record<string, string> = Object.assign(Object.create(null), { filetype: DOCICON_FIELD_NAME });

export const normaliseKey = (key: string): string => {
  const trimmed = key.trim();
  return FIELD_ALIASES[trimmed.toLowerCase()] ?? trimmed;
};

// Case-insensitive match on either the internal (RealFieldName) or display name.
// `key` must already be normalised (see normaliseKey).
export const matchesFieldKey = (field: any, key: string): boolean => {
  const target = key.toLowerCase();
  return field?.RealFieldName?.toLowerCase() === target || field?.DisplayName?.toLowerCase() === target;
};

export const findSchemaField = (schemaFields: any[], rawKey: string): any | undefined => {
  const key = normaliseKey(rawKey);
  return (schemaFields ?? []).find(field => matchesFieldKey(field, key));
};

export const findSectionByKey = (sections: FilterSection[], rawKey: string): FilterSection | undefined => {
  const key = normaliseKey(rawKey);
  return (sections ?? []).find(section => matchesFieldKey(section?.filterField, key));
};

// RenderListDataAsStream schema fields carry the type as FieldType or Type
// depending on origin; every predicate below accepts either.
const fieldTypeIn = (field: any, types: Set<string>): boolean =>
  types.has(field?.FieldType) || types.has(field?.Type);

const DATETIME_FIELD_TYPES = new Set(["DateTime"]);

// DateTime pre-selects are unsupported (v1): the filter UI models dates as slider
// offsets, not discrete values, so a querystring date has nothing to resolve to.
export const isDateTimeField = (field: any): boolean =>
  fieldTypeIn(field, DATETIME_FIELD_TYPES);

// Fields deliberately excluded from the filter surface — the same exclusions the
// section build applies (FileFilterHelper.setFilterSectionsFromFields, plus the
// base ID column buildDisplayFilterSections drops). The pre-select pipeline must
// honour them everywhere sections are synthesized: a deep link naming a hidden
// field must NOT resurrect it as an applied filter.
export const isExcludedFilterField = (field: any, hideTitleFieldFilter?: boolean): boolean =>
  field?.RealFieldName === "FileSizeDisplay"
  || field?.RealFieldName === "_UIVersionString"
  || field?.Filterable === "FALSE"
  || field?.ShowInFiltersPane === "FALSE"
  || field?.GroupField === "TRUE"
  || (field?.RealFieldName === "ID" && field?.FromBaseType === "TRUE")
  || (!!hideTitleFieldFilter && field?.RealFieldName === "Title");

// SharePoint numeric filter fields whose RenderListFilterData value is the
// decimal-normalised form (e.g. "2026.00000000000") while the fast path
// synthesises the raw querystring form ("2026"). A Number query with FilterOp=In
// matches by numeric magnitude, so those two representations are the SAME query —
// signature comparison must treat them as equal or the reconcile fires a redundant
// corrective re-fetch (a visible second shimmer).
const NUMERIC_FIELD_TYPES = new Set(["Number", "Currency", "Counter", "Integer"]);

export const isNumericField = (field: any): boolean =>
  fieldTypeIn(field, NUMERIC_FIELD_TYPES);

// Field families whose SharePoint FilterValue can be an internal token/ID rather
// than the human-typed display text — so the fast (raw-value) fetch #1 may return
// nothing and only reconcile (which reads the real token) can filter correctly.
const TOKEN_RISKY_FIELD_TYPES = new Set([
  "Lookup", "LookupMulti", "User", "UserMulti", "TaxonomyFieldType", "TaxonomyFieldTypeMulti",
]);

export const isTokenRiskyField = (field: any): boolean =>
  fieldTypeIn(field, TOKEN_RISKY_FIELD_TYPES);

// The type string FilterType{n}= / <Value Type=...> emission puts on the wire.
// Sections from the real build carry it as Type; schema-origin sections (the
// fast path and the build-loss fallback synthesize straight from setup schema
// fields) may expose it only as FieldType. Emitting through this accessor keeps
// "FilterType2=undefined" off the URL for both origins.
export const getFieldWireType = (field: any): string => field?.Type ?? field?.FieldType ?? "";

// The ONE normalisation for a querystring file-type value: how the fast path,
// the reconcile's matcher and its raw injection all read an extension. A single
// definition keeps the three sites parity-safe by construction (" .PDF" and
// "pdf" are the same query).
export const normalizeExtension = (value: string): string =>
  value.trim().toLowerCase().replace(/^\./, "");

// Office file-type sections are compacted into a single bucket item per app whose
// `fileType` is the modern representative extension (docx/xlsx/pptx/vsdx) and whose
// `values` is the app's pre-encoded extension list — mirroring the DocIcon bucket
// construction in FileFilterHelper.setFilterSectionsFromFields. Map every grouped
// extension (e.g. doc, xls, ppt, vsd, odt, csv, …) to its bucket so a legacy
// extension in the querystring still resolves.
export interface OfficeBucket {
  title: string;
  values: string;
  fileType: string;
}

export const buildOfficeBucketLookup = (office: typeof officeExtentionsType): Record<string, OfficeBucket> => {
  // Null prototype: keys come straight from the querystring, so an extension
  // named like an Object.prototype member ("constructor", "toString", …) must
  // miss the lookup instead of returning an inherited function that passes an
  // `if (bucket)` check and crashes the consumer.
  const lookup: Record<string, OfficeBucket> = Object.create(null);
  const buckets: [{ items: string[]; values: string }, string, string][] = [
    [office.word, "docx", "Word"],
    [office.excel, "xlsx", "Excel"],
    [office.powerPoint, "pptx", "PowerPoint"],
    [office.visio, "vsdx", "Visio"],
  ];
  buckets.forEach(([bucket, fileType, title]) => {
    bucket.items.forEach(ext => {
      lookup[ext.toLowerCase()] = { title, values: bucket.values, fileType };
    });
    // The bucket TITLE resolves too (?filetype=Word): panel item toggles match
    // by title, so a raw injection titled "Word" next to the bucket item would
    // create two identically-titled rows that flip together. Resolving titles
    // in the shared lookup keeps the fast path and the reconcile in parity by
    // construction — and matches what a hand-written deep link means anyway.
    lookup[title.toLowerCase()] = { title, values: bucket.values, fileType };
  });
  return lookup;
};
